<template>
  <div class="resume-public min-h-screen">
    <div class="resume-public__atmosphere" aria-hidden="true" />

    <div class="resume-public__shell">
      <div class="resume-toolbar no-print">
        <div class="min-w-0">
          <p class="resume-toolbar__eyebrow">Resume</p>
          <h1 class="resume-toolbar__title">{{ resume.title }}</h1>
          <p v-if="resume.views !== undefined" class="resume-toolbar__meta">
            <icon name="aperture" class="w-3.5 h-3.5" />
            {{ resume.views }} {{ resume.views === 1 ? 'view' : 'views' }}
          </p>
        </div>
        <div class="resume-toolbar__actions">
          <button
            @click="shareResume"
            :disabled="copied"
            class="resume-action resume-action--ghost"
            :class="{ 'is-success': copied }"
          >
            <icon v-if="!copied" name="send" class="w-4 h-4" />
            <icon v-else name="tick" class="w-4 h-4" />
            {{ copied ? 'Copied' : 'Share' }}
          </button>
          <button
            @click="downloadPDF"
            :disabled="downloading"
            class="resume-action resume-action--solid"
          >
            <icon v-if="!downloading" name="download" class="w-4 h-4" />
            <svg v-else class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ downloading ? 'Generating…' : 'Download PDF' }}
          </button>
        </div>
      </div>

      <article
        id="resume-content"
        class="resume-document ps-animate-in"
        :class="layoutClass"
        :style="documentStyle"
      >
        <div v-if="isTwoColumn" class="resume-document__grid">
          <aside class="resume-document__sidebar">
            <component
              v-for="section in sidebarSections"
              :key="section.id"
              :is="getSectionComponent(section.type)"
              :section="section"
              :theme-color="resume.theme_color"
              :compact="true"
            />
          </aside>
          <div class="resume-document__main">
            <component
              v-for="section in mainSections"
              :key="section.id"
              :is="getSectionComponent(section.type)"
              :section="section"
              :theme-color="resume.theme_color"
            />
          </div>
        </div>
        <template v-else>
          <component
            v-for="section in sortedSections"
            :key="section.id"
            :is="getSectionComponent(section.type)"
            :section="section"
            :theme-color="resume.theme_color"
          />
        </template>
      </article>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import ProfileSection from './Sections/ProfileSection.vue'
import ExperienceSection from './Sections/ExperienceSection.vue'
import EducationSection from './Sections/EducationSection.vue'
import SkillsSection from './Sections/SkillsSection.vue'
import ProjectsSection from './Sections/ProjectsSection.vue'

const SIDEBAR_TYPES = ['skills', 'education']

export default {
  components: {
    Icon,
    ProfileSection,
    ExperienceSection,
    EducationSection,
    SkillsSection,
    ProjectsSection,
  },
  props: {
    resume: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Resume'
    }
  },
  data() {
    return {
      downloading: false,
      copied: false,
    }
  },
  computed: {
    sortedSections() {
      return [...this.sections].sort((a, b) => a.order - b.order)
    },
    isTwoColumn() {
      return this.resume.layout === 'two-column'
    },
    layoutClass() {
      return this.isTwoColumn ? 'is-two-column' : 'is-one-column'
    },
    documentStyle() {
      const font = this.resume.font_family || 'DM Sans'
      const mapped = {
        Inter: 'var(--ps-font-resume-body)',
        Roboto: 'var(--ps-font-resume-body)',
        'Open Sans': 'var(--ps-font-resume-body)',
        Lato: 'var(--ps-font-resume-body)',
        Montserrat: 'var(--ps-font-display)',
        'Source Serif 4': 'var(--ps-font-resume-display)',
        'DM Sans': 'var(--ps-font-resume-body)',
        Outfit: 'var(--ps-font-display)',
      }
      return {
        '--resume-accent': this.resume.theme_color || '#0f766e',
        fontFamily: mapped[font] || font,
        color: '#1e293b',
      }
    },
    profileSection() {
      return this.sortedSections.find((section) => section.type === 'profile')
    },
    sidebarSections() {
      const rest = this.sortedSections.filter((section) => SIDEBAR_TYPES.includes(section.type))
      return this.profileSection ? [this.profileSection, ...rest] : rest
    },
    mainSections() {
      return this.sortedSections.filter(
        (section) => section.type !== 'profile' && !SIDEBAR_TYPES.includes(section.type)
      )
    },
  },
  methods: {
    getSectionComponent(type) {
      switch (type) {
        case 'profile':
          return 'ProfileSection'
        case 'experience':
          return 'ExperienceSection'
        case 'education':
          return 'EducationSection'
        case 'skills':
          return 'SkillsSection'
        case 'projects':
          return 'ProjectsSection'
        default:
          return 'div'
      }
    },
    async downloadPDF() {
      this.downloading = true
      try {
        const html2pdf = (await import('html2pdf.js')).default
        const element = document.getElementById('resume-content')
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `${this.resume.title || 'resume'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }

        await html2pdf().set(opt).from(element).save()
        this.downloading = false
      } catch (error) {
        console.error('Error generating PDF:', error)
        if (window.$toast) {
          window.$toast.error('Failed to generate PDF. Please try again.')
        }
        this.downloading = false
      }
    },
    async shareResume() {
      const url = window.location.href
      try {
        if (navigator.share) {
          await navigator.share({
            title: this.resume.title,
            text: `Check out my resume: ${this.resume.title}`,
            url: url
          })
        } else {
          await navigator.clipboard.writeText(url)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 3000)
        }
      } catch (error) {
        try {
          await navigator.clipboard.writeText(url)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 3000)
        } catch (err) {
          console.error('Failed to share:', err)
        }
      }
    }
  },
  watch: {
    'resume.title': {
      immediate: true,
      handler(newTitle) {
        document.title = newTitle ? `${newTitle} - Resume` : 'Resume'
      }
    }
  }
}
</script>

<style scoped>
.resume-public {
  position: relative;
  min-height: 100vh;
  background: #eef2ef;
  font-family: var(--ps-font-body);
}

.resume-public__atmosphere {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 70% 40% at 50% -5%, rgba(15, 118, 110, 0.14), transparent 55%),
    linear-gradient(180deg, #f7faf8 0%, #e8eee9 100%);
}

.resume-public__shell {
  position: relative;
  z-index: 1;
  max-width: 56rem;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
}

@media (min-width: 640px) {
  .resume-public__shell {
    padding: 2rem 1.5rem 4rem;
  }
}

.resume-toolbar {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

@media (min-width: 640px) {
  .resume-toolbar {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.resume-toolbar__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0f766e;
  margin-bottom: 0.25rem;
}

.resume-toolbar__title {
  font-family: var(--ps-font-display);
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resume-toolbar__meta {
  margin-top: 0.35rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #64748b;
}

.resume-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  width: 100%;
}

@media (min-width: 640px) {
  .resume-toolbar__actions {
    width: auto;
  }
}

.resume-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  flex: 1;
  padding: 0.65rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

@media (min-width: 640px) {
  .resume-action {
    flex: none;
  }
}

.resume-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.resume-action--ghost {
  background: rgba(255, 255, 255, 0.75);
  color: #334155;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.resume-action--ghost:hover:not(:disabled) {
  background: #fff;
}

.resume-action--ghost.is-success {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.resume-action--solid {
  background: #0f766e;
  color: #fff;
  box-shadow: 0 10px 20px -12px rgba(15, 118, 110, 0.7);
}

.resume-action--solid:hover:not(:disabled) {
  background: #0d9488;
  transform: translateY(-1px);
}

.resume-document {
  --resume-accent: #0f766e;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.7) inset,
    0 24px 48px -28px rgba(15, 23, 42, 0.35);
  padding: 1.5rem;
  line-height: 1.6;
}

@media (min-width: 640px) {
  .resume-document {
    padding: 2.5rem 3rem;
  }
}

@media (min-width: 768px) {
  .resume-document {
    padding: 3rem 3.5rem;
  }
}

.resume-document.is-two-column .resume-document__grid {
  display: grid;
  gap: 1.75rem;
}

@media (min-width: 768px) {
  .resume-document.is-two-column .resume-document__grid {
    grid-template-columns: minmax(11rem, 0.34fr) 1fr;
    gap: 2.25rem;
  }

  .resume-document.is-two-column .resume-document__sidebar {
    border-right: 1px solid color-mix(in srgb, var(--resume-accent) 28%, #e2e8f0);
    padding-right: 1.5rem;
  }
}

.resume-document__sidebar :deep(.resume-section + .resume-section),
.resume-document__main :deep(.resume-section + .resume-section),
.resume-document.is-one-column :deep(.resume-section + .resume-section) {
  margin-top: 1.75rem;
}

@media print {
  .no-print {
    display: none !important;
  }

  .resume-public {
    background: #fff;
  }

  .resume-public__atmosphere {
    display: none;
  }

  .resume-public__shell {
    max-width: none;
    padding: 0;
  }

  .resume-document {
    box-shadow: none;
    border: none;
    padding: 0;
  }
}
</style>
